/**
 * Theme provider. Resolves `override → prefers-color-scheme` via the pure
 * {@link resolveTheme}, stamps the result as `data-theme` on a target element
 * (the document root by default) so the SCSS token maps re-bind, PERSISTS the
 * override to `localStorage['theme']`, and exposes all of it through context.
 * No component branches on theme in JS — they read tokens.
 *
 * Controlled or uncontrolled, without a mode flag:
 *   - pass `override` and it wins (previous behaviour, unchanged) and is
 *     persisted for you;
 *   - omit it and the provider seeds itself from storage and owns the value,
 *     which `useTheme().setOverride(…)` then drives.
 */
import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from 'solid-js';
import { MissingProviderError } from '../errors/index.ts';
import { type ResolvedTheme, resolveTheme, type ThemeOverride } from './resolve-theme.ts';
import {
  defaultThemeStorage,
  readThemeOverride,
  THEME_STORAGE_KEY,
  type ThemeStorage,
  writeThemeOverride,
} from './storage.ts';

export interface ThemeContextValue {
  /** The concrete theme currently applied. */
  readonly theme: Accessor<ResolvedTheme>;
  /** True when the OS reports a dark preference. */
  readonly prefersDark: Accessor<boolean>;
  /** The user's choice (`auto` follows the OS) — what a settings control binds to. */
  readonly override: Accessor<ThemeOverride>;
  /** Set + persist the user's choice. `auto` clears the stored override. */
  readonly setOverride: (override: ThemeOverride) => void;
}

const ThemeContext = createContext<ThemeContextValue>();

export interface ThemeProviderProps {
  /** User choice; `auto` follows the OS. Reactive. Defaults to the persisted value. */
  readonly override?: ThemeOverride;
  /** Element to stamp `data-theme` on. Defaults to `document.documentElement`. */
  readonly target?: HTMLElement;
  /** Persist the override across reloads. Defaults to `true`. */
  readonly persist?: boolean;
  /** Storage key. Defaults to `theme` — the key marketing's bootstrap reads. */
  readonly storageKey?: string;
  /** Storage backend. Defaults to `localStorage`; injected in tests. */
  readonly storage?: ThemeStorage;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function prefersDarkNow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

export const ThemeProvider: ParentComponent<ThemeProviderProps> = (props) => {
  const [prefersDark, setPrefersDark] = createSignal<boolean>(prefersDarkNow());

  const storage = (): ThemeStorage | undefined => {
    if (props.persist === false) return undefined;
    return props.storage ?? defaultThemeStorage();
  };
  const key = (): string => props.storageKey ?? THEME_STORAGE_KEY;

  // Seeded once from storage so the very first render already matches what the
  // pre-paint bootstrap stamped — no flash, no second resolution pass.
  const [stored, setStored] = createSignal<ThemeOverride>(readThemeOverride(storage(), key()));

  // A supplied `override` prop always wins (the provider stays controllable);
  // otherwise the persisted/locally-set value drives.
  const override = createMemo<ThemeOverride>(() => props.override ?? stored());

  createEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setPrefersDark(event.matches);
    };
    mql.addEventListener('change', onChange);
    onCleanup(() => mql.removeEventListener('change', onChange));
  });

  // A controlled consumer gets persistence for free — so a settings screen that
  // owns `override` in its own store still leaves marketing's reader a value.
  createEffect(() => {
    const controlled = props.override;
    if (controlled === undefined) return;
    writeThemeOverride(storage(), controlled, key());
  });

  const setOverride = (next: ThemeOverride): void => {
    setStored(next);
    writeThemeOverride(storage(), next, key());
  };

  const theme = createMemo<ResolvedTheme>(() => resolveTheme(override(), prefersDark()));

  createEffect(() => {
    const target =
      props.target ?? (typeof document !== 'undefined' ? document.documentElement : undefined);
    target?.setAttribute('data-theme', theme());
  });

  return (
    <ThemeContext.Provider value={{ theme, prefersDark, override, setOverride }}>
      {props.children}
    </ThemeContext.Provider>
  );
};

/** Access the resolved theme. Throws when used outside {@link ThemeProvider}. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new MissingProviderError('useTheme', 'ThemeProvider');
  return ctx;
}
